import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { Hexagon } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { user, loading, pendingEmailLink, emailLinkError, signInWithGoogle, sendEmailLink, completeEmailLink } = useAuth();
  const [email, setEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  if (user) return <Navigate to="/" replace />;

  const handleGoogle = async () => {
    setError("");
    try {
      await signInWithGoogle();
    } catch (err: any) {
      if (err?.code === "auth/popup-closed-by-user") return;
      if (err?.code === "auth/popup-blocked") { setError("Popup was blocked by your browser. Allow popups for this site or try the email sign-in option below."); return; }
      if (err?.message?.includes("not registered") || err?.message?.includes("PERMISSION_DENIED")) { setError("You are not registered in the program. Contact your program manager."); return; }
      setError(err?.message || "Sign-in failed. Please try again.");
    }
  };

  const handleSendLink = async () => {
    if (!email) return;
    setError("");
    setBusy(true);
    try {
      await sendEmailLink(email);
      setLinkSent(true);
    } catch (err: any) {
      setError(err?.message?.includes("not registered") ? "This email is not registered in the program." : err?.message || "Failed to send sign-in link.");
    } finally {
      setBusy(false);
    }
  };

  const handleCompleteLink = async () => {
    if (!email) return;
    setError("");
    setBusy(true);
    try {
      await completeEmailLink(email);
    } catch (err: any) {
      setError(err?.message || "Failed to complete sign-in. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="w-full max-w-sm p-8 text-center space-y-6">
        <div className="flex flex-col items-center gap-2">
          <Hexagon className="w-12 h-12 text-primary" />
          <h1 className="text-xl font-bold">Africa K8s Pulse</h1>
          <p className="text-xs text-muted-foreground">Kubernetes African Developer Training Program 2026</p>
          <p className="text-[10px] text-muted-foreground">An Andela and CNCF partnership initiative</p>
        </div>

        {/* Cross-device email link completion */}
        {pendingEmailLink ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">Confirm your email to complete sign-in</p>
            <p className="text-xs text-muted-foreground">You opened this sign-in link on a different device. Enter your email to continue.</p>
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCompleteLink()}
              className="w-full h-10 px-3 text-sm border rounded-md bg-background"
              autoFocus
            />
            <Button onClick={handleCompleteLink} className="w-full" size="lg" disabled={!email || busy}>
              {busy ? "Signing in..." : "Complete sign-in"}
            </Button>
            {error && <p className="text-xs text-health-red" role="alert">{error}</p>}
          </div>
        ) : linkSent ? (
          <div className="space-y-2">
            <p className="text-sm text-health-green font-medium">Sign-in link sent!</p>
            <p className="text-xs text-muted-foreground">Check your <span className="font-bold text-foreground">Inbox</span>, <span className="font-bold text-foreground">Spam</span>, or <span className="font-bold text-foreground">Junk</span> folder at <span className="font-medium">{email}</span> and click the link to sign in.</p>
            <button onClick={() => { setLinkSent(false); setEmail(""); setError(""); }} className="text-xs text-primary underline">Use a different email</button>
          </div>
        ) : (
          <>
            <Button onClick={handleGoogle} className="w-full gap-2" size="lg">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Sign in with Google
            </Button>

            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex-1 h-px bg-border" />
              <span>or sign in with email</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div className="space-y-3">
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendLink()}
                className="w-full h-10 px-3 text-sm border rounded-md bg-background"
              />
              <Button onClick={handleSendLink} variant="outline" className="w-full" size="lg" disabled={!email || busy}>
                {busy ? "Sending..." : "Send sign-in link"}
              </Button>
            </div>

            {(error || emailLinkError) && <p className="text-xs text-health-red" role="alert">{error || emailLinkError}</p>}
          </>
        )}
      </div>
    </div>
  );
}
